define([
	"dojo/_base/declare",
	"underscore",
	"csi/def",
	"csi/heading/Number",
	"csi/heading/Renderer",
	"csi/heading/stateColorResolver",
	"csi/heading/String",
	"csi/locale",
	"csi/reflection",
	"csi/table/Widget",
	"csi/task/util",
	"csi/types",
	"csi/widget/PillAnchor",
	"csi/xml",
	"csi/xml/Query",
	"./_ToolbarSelectMixin"
], function(declare, _, def, NumberRen, Renderer, stateColorResolver, StringRen, locale, reflection, TableWidget, TaskUtil, types, PillAnchor, xml, Query, _ToolbarSelectMixin) {

	const FILTER_ALL = "CamundaFilterAllProcessDefinition";
	const FILTER_DRAFT = "CamundaFilterDraftProcessDefinition";
	const FILTER_PUBLISHED = "CamundaFilterPublishedProcessDefinition";

	const TYPE_REVISION = "CamundaProcessDefinitionRevision";

	/**
	 * Render version pill for given revision
	 * @param {Node} revision
	 * @param {Node} current
	 * @param {DOMNode} refNode
	 */
	function pill(revision, current, refNode) {
		const version = revision.Version;
		const params = {
			href: def.getNodeBrowseURL(revision)
		};

		if (version === 0) {
			params["class"] = stateColorResolver.getStateCssByIndex(TYPE_REVISION, "Draft");
			params.value = locale.resource("Draft");
		} else if (revision === current) {
			params["class"] = stateColorResolver.getStateCssByIndex(TYPE_REVISION, "Published");
			params.value = locale.resource("CamundaVersionLastPublished", [version]);
		} else {
			params["class"] = "csi-name-value-pair-transparent";
			params.value = locale.resource("CamundaVersion", [version]);
		}

		new PillAnchor(params).placeAt(refNode);
	}

	const _NameRenderer = declare(StringRen, {
		/**
		 * @override
		 * Render nothing for revision
		 */
		headingToHtml: function(ctx, h, refNode) {
			if (ctx.getNode().$Type !== TYPE_REVISION) {
				this.inherited(arguments);
			}
		}
	});

	const _NumberRenderer = declare(NumberRen, {
		/**
		 * @override
		 * Render dashes if rendering number value for Draft version
		 */
		toValue: function(ctx, h) {
			const node = ctx.getNode();

			if ((node.$Type === TYPE_REVISION) && (node.Version === 0)) {
				return "--";
			}

			return this.inherited(arguments);
		}
	});

	const _VersionRenderer = declare(Renderer, {
		/**
		 * @override
		 * Render pill(s) for version
		 */
		toHtmlStatic: function(ctx, h, refNode, actionData) {
			const cache = ctx.cache;
			const node = ctx.getNode();
			const master = cache.get(node.Master);
			const latest = cache.get(master.LatestRevision);
			const current = cache.get(master.CurrentRevision);

			if (node === master) {
				// render pills for draft and/or last published revisions
				if (latest !== current) {
					pill(latest, current, refNode);
				}

				pill(current, current, refNode);
			} else {
				// render pill for current revision
				pill(node, current, refNode);
			}
		}
	});

	return declare([TableWidget, _ToolbarSelectMixin], {
		"class": "csi-camunda-process-placeholder csi-table-align-middle csi-table-merge-hierarchy",
		camundaFilterValues: [FILTER_ALL, FILTER_PUBLISHED, FILTER_DRAFT],
		selectWithHierarchy: true,

		/**
		 * @override
		 * Add headings to view
		 */
		postMixInProperties: function() {
			const defView = this.defView;

			function newHeading(params) {
				return _.extend(defView.newHeading(), params);
			}

			defView.m_headings = [
				newHeading({
					m_id: xml.ATTR_NAME,
					classRenderer: _NameRenderer
				}),
				newHeading({
					m_id: "Key"
				}),
				newHeading({
					m_id: "PrimaryType"
				}),
				newHeading({
					m_id: "PrimarySubtype"
				}),
				newHeading({
					m_id: "Active"
				}),
				newHeading({
					m_id: "ManualStartAllowed"
				}),
				newHeading({
					m_id: "MultipleInstancesAllowed"
				}),
				newHeading({
					m_name: "Version",
					classRenderer: _VersionRenderer,
					columnWidth: types.MEDIUM
				}),
				newHeading({
					m_id: "NumberOfRunningInstances",
					classRenderer: _NumberRenderer
				}),
				newHeading({
					m_id: "NumberOfRunningUserTasks",
					classRenderer: _NumberRenderer
				})
			];

			this.inherited(arguments);
		},

		/**
		 * Allow access to workflow admins
		 * @param {Context} ctx 
		 * @param {Action} action 
		 * @return {boolean}
		 */
		onAccessActionState(ctx, action) {
			return TaskUtil.isWorkflowAdmin(ctx);
		},

		/**
		 * Publish latest revision of CamundaProcessDefinition
		 * @param {Controller} ac
		 */
		onActionPublishProcess: function(ac) {
			ac.m_writer.callMethod("SiteAdmin", "Approve").addParameter(xml.PROP_URL, xml.ATTR_REF, ac.getNode().LatestRevision);
		},

		/**
		 * Draft CamundaProcessDefinition revision, delete the whole thing if it only has one revision and that's draft
		 * @param {Controller} ac
		 */
		onActionDiscardProcess: function(ac) {
			const processDefinition = ac.getNode();
			const latestRevisionURL = processDefinition.LatestRevision;
			const masterURL = processDefinition.Master;

			ac.m_writer.deleteNode(processDefinition.Revisions.length > 1 ? latestRevisionURL : masterURL);
		},

		/**
		 * "Refresh" the UI once the request completes if needed
		 * @param {Controller} ac
		 */
		postActionDiscardProcess: function(ac) {
			const row = this.getRefNode(ac.refNode || ac.m_element);

			if (!ac.getNode()) {
				// in the case of deleting the processDefinition only has one draft revision
				this.deleteRow(row);
			} else {
				this.refreshHierarchyRow(row);
			}
		},

		/**
		 * @override
		 * Set server query based on toolbar filter selection
		 */
		onCreateMainQueryLevel: function(ql) {
			const value = this.camundaFilterValue;
			var state;
			var path;

			if (value === FILTER_DRAFT) {
				state = reflection.REVISION_DRAFT;
				path = reflection.PATH_LATEST_REVISION;
			} else if (value === FILTER_PUBLISHED) {
				state = reflection.REVISION_APPROVED;
				path = reflection.PATH_CURRENT_REVISION;
			}

			ql.serverQuery = state ? new Query().Attr(reflection.ATTR_STATE, xml.OP_EQUALS, state, path) : null;
		},

		/**
		 * @override
		 * Return custom type for CamundaProcessDefinition
		 */
		_getNoResultType: function() {
			return "ProcessDefinition";
		},

		/**
		 * @override
		 * Return custom message for CamundaProcessDefinition
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			return locale.resource("ViewPlaceholderDescriptionProcessDefinition");
		}
	});
});